import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthTokenService } from '../core/auth/auth-token.service';

@Injectable({
  providedIn: 'root'
})
export class SessionIdleService implements OnDestroy {
  private static readonly LAST_REFRESH_KEY = 'jwt_last_refresh';
  /** Rotate access+refresh about hourly while the user is active (sliding 30-day window). */
  private static readonly REFRESH_INTERVAL_MS = 60 * 60 * 1000;
  private static readonly ACTIVITY_THROTTLE_MS = 60 * 1000;

  private refreshing = false;
  private lastActivityCheck = 0;
  private readonly activityHandler = () => this.onUserActivity();
  private readonly activityEvents = ['click', 'keydown', 'touchstart', 'mousemove', 'scroll'];

  constructor(
    private authTokenService: AuthTokenService,
    private ngZone: NgZone
  ) {}

  start(): void {
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.activityHandler, { passive: true });
      });
    });
    this.tryRefreshTokens();
  }

  clear(): void {
    localStorage.removeItem(SessionIdleService.LAST_REFRESH_KEY);
  }

  tryRefreshTokens(force: boolean = false): void {
    if (this.refreshing) {
      return;
    }

    if (!this.authTokenService.hasValidRefreshToken()) {
      return;
    }

    const lastRefresh = Number(localStorage.getItem(SessionIdleService.LAST_REFRESH_KEY) || '0');
    const dueByInterval = !lastRefresh
      || (Date.now() - lastRefresh) >= SessionIdleService.REFRESH_INTERVAL_MS;

    if (!force && this.authTokenService.hasValidAccessToken() && !dueByInterval) {
      return;
    }

    this.refreshing = true;
    const shouldForce = force || this.authTokenService.hasValidAccessToken();
    this.authTokenService.refreshAccessToken(shouldForce).subscribe({
      next: () => {
        this.refreshing = false;
      },
      error: () => {
        this.refreshing = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.activityEvents.forEach(eventName => {
      window.removeEventListener(eventName, this.activityHandler);
    });
  }

  private onUserActivity(): void {
    const now = Date.now();
    if ((now - this.lastActivityCheck) < SessionIdleService.ACTIVITY_THROTTLE_MS) {
      return;
    }
    this.lastActivityCheck = now;
    this.ngZone.run(() => {
      this.tryRefreshTokens();
    });
  }
}
